import connector from 'api/http';
import { ApiData, Excess } from 'scenes/common/types';
import config from 'shared/config';
import {
  MotorQuoteDetails,
  MotorFunnelState,
  MarketAgreedValue,
  MotorCoverType,
  ReferenceData,
} from 'scenes/motor/types';
import { makeApiQuoteSaved } from 'scenes/common/helpers/apiData';
import {
  MotorInsuranceType,
  OptionalExtra,
} from 'scenes/motor/flows/coverOptions/types';
import { ApiName } from 'scenes/common/api/quote';

import {
  unmarshalQuoteResponse,
  marshalQuoteCreateRequest,
  marshalQuoteUpdateExcess,
  marshalQuoteUpdateVehicleValue,
  marshalQuoteUpdateOptionalExtras,
  marshalQuoteUpdateInsuranceType,
  marshalQuoteUpdateInsuranceOption,
  marshalQuoteUpdateToPolicyDetails,
  marshalQuoteUpdateToDataComplete,
  marshalQuoteUpdateDetails,
  marshalQuoteUpdateEmail,
  marshalQuoteUpdateSave,
  marshalQuoteUpdatePolicyStartDate,
} from './marshaller';
import { checkPromoCode, handleError } from './services/quote';

const api = (baseUrl: string = config.baseUrls.BFF_BASE_URL) => {
  const server = connector(baseUrl);

  return {
    createQuote: (
      funnelState: MotorFunnelState,
      referenceData: ReferenceData
    ): Promise<ApiData<MotorQuoteDetails>> =>
      Promise.resolve(funnelState)
        .then(marshalQuoteCreateRequest)
        .then((request) => server.post('/quote/motor/create', request))
        .then((response) => checkPromoCode(funnelState, response))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.CreateQuote, funnelState, referenceData)
        ),

    updateQuoteDetails: (
      funnelState: MotorFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(funnelState)
        .then(marshalQuoteUpdateDetails)
        .then((request) => server.post('/quote/motor/update', request))
        .then((response) => checkPromoCode(funnelState, response))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, funnelState, referenceData)
        ),

    updatePolicyStartDate: (
      funnelState: MotorFunnelState,
      policyStartDate: string,
      referenceData: ReferenceData
    ) =>
      Promise.resolve()
        .then(() =>
          marshalQuoteUpdatePolicyStartDate(funnelState, policyStartDate)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, funnelState, referenceData)
        ),

    updateInsuranceType: (
      motorFunnelState: MotorFunnelState,
      motorInsuranceType: MotorInsuranceType,
      motorCoverType: MotorCoverType,
      referenceData: ReferenceData,
      removeExtras?: Array<OptionalExtra>
    ) =>
      Promise.resolve({
        funnelState: motorFunnelState,
        insuranceType: motorInsuranceType,
        coverType: motorCoverType,
      })
        .then(({ funnelState, insuranceType, coverType }) =>
          marshalQuoteUpdateInsuranceType(
            funnelState,
            insuranceType,
            coverType,
            removeExtras
          )
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, motorFunnelState, referenceData)
        ),

    updateInsuranceOption: (
      motorFunnelState: MotorFunnelState,
      motorCoverType: MotorCoverType,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({
        funnelState: motorFunnelState,
        coverType: motorCoverType,
      })
        .then(({ funnelState, coverType }) =>
          marshalQuoteUpdateInsuranceOption(funnelState, coverType)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, motorFunnelState, referenceData)
        ),

    updateExcess: (
      motorFunnelState: MotorFunnelState,
      motorExcess: Excess,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: motorFunnelState, excess: motorExcess })
        .then(({ funnelState, excess }) =>
          marshalQuoteUpdateExcess(funnelState, excess)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, motorFunnelState, referenceData)
        ),

    updateVehicleSumInsured: (
      motorFunnelState: MotorFunnelState,
      agreedValue: string,
      marketOrAgreed: MarketAgreedValue,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({
        funnelState: motorFunnelState,
        agreedVehicleValue: agreedValue,
        marketOrAgreedValue: marketOrAgreed,
      })
        .then(({ funnelState, agreedVehicleValue, marketOrAgreedValue }) =>
          marshalQuoteUpdateVehicleValue(
            funnelState,
            agreedVehicleValue,
            marketOrAgreedValue
          )
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, motorFunnelState, referenceData)
        ),

    updateOptionalExtras: (
      motorFunnelState: MotorFunnelState,
      selectedExtras: string[],
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: motorFunnelState, extras: selectedExtras })
        .then(({ funnelState, extras }) =>
          marshalQuoteUpdateOptionalExtras(funnelState, extras)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, motorFunnelState, referenceData)
        ),

    updateToPolicyDetails: (
      funnelState: MotorFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(funnelState)
        .then(marshalQuoteUpdateToPolicyDetails)
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, funnelState, referenceData)
        ),

    updateToDataComplete: (
      funnelState: MotorFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(funnelState)
        .then(marshalQuoteUpdateToDataComplete)
        .then((request) => server.post('/quote/motor/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) =>
          handleError(err, ApiName.UpdateQuote, funnelState, referenceData)
        ),

    updateToSaveQuote: (
      motorFunnelState: MotorFunnelState,
      emailAddress: string,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: motorFunnelState, email: emailAddress })
        .then(({ funnelState, email }) =>
          marshalQuoteUpdateSave(funnelState, email)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(() =>
          makeApiQuoteSaved({
            ...motorFunnelState.apiResponses.quoteDetails.data,
            saveQuoteEmailAddress: emailAddress,
          })
        )
        .catch((err) =>
          Promise.reject(
            handleError(err, ApiName.SaveQuote, motorFunnelState, referenceData)
          )
        ),

    updateToEmailQuote: (
      motorFunnelState: MotorFunnelState,
      emailAddress: string,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: motorFunnelState, email: emailAddress })
        .then(({ funnelState, email }) =>
          marshalQuoteUpdateEmail(funnelState, email)
        )
        .then((request) => server.post('/quote/motor/update', request))
        .then(() =>
          makeApiQuoteSaved({
            ...motorFunnelState.apiResponses.quoteDetails.data,
            saveQuoteEmailAddress: emailAddress,
          })
        )
        .catch((err) =>
          Promise.reject(
            handleError(err, ApiName.SaveQuote, motorFunnelState, referenceData)
          )
        ),
  };
};

export default api;
